import { useState, useEffect, useCallback, useMemo } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useNavigate } from "react-router-dom";
import { useRecordDetails } from "@/hooks/useRecordDetails";
import { useRecordActions } from "@/hooks/useRecordActions";
import { useDeleteRecord } from "@/hooks/useDeleteRecord";
import { useRecordDialogs } from "@/hooks/useRecordDialogs";
import { useRecordHandlers } from "@/hooks/useRecordHandlers";
import { RecordHeader } from "@/components/records/RecordHeader";
import { RecordTabsSection } from "@/components/records/RecordTabsSection";
import { RecordDialogs } from "@/components/records/RecordDialogs";
import { LoadingState } from "@/components/records/LoadingState";
import { RecordNotFound } from "@/components/records/RecordNotFound";
import { ShareDialog } from "@/components/sharing/ShareDialog";
import { ShareManagementDialog } from "@/components/sharing/ShareManagementDialog";
import { useSharedLinksExtended } from "@/hooks/useSharedLinksExtended";
import type { SharedLink } from "@/integrations/supabase/types";
import type { ActiveTab } from "@/components/records/types/recordHeader.types";

const RecordDetails = () => {
  const navigate = useNavigate();
  const { record, loading, updateProcessedText, refetchRecord } = useRecordDetails();
  const [activeTab, setActiveTab] = useState<ActiveTab>("processed");
  const [localRecord, setLocalRecord] = useState(record);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareManagementDialogOpen, setShareManagementDialogOpen] = useState(false);
  const [currentSharedLink, setCurrentSharedLink] = useState<SharedLink | null>(null);
  // Получаем статус публикации из данных записи
  const [isPublished, setIsPublished] = useState(record?.isPublished || false);
  const [shareUrl, setShareUrl] = useState<string | null>(record?.shareUrl || null);
  // Состояние редактирования для блокировки UI
  const [isEditing, setIsEditing] = useState(false);
  const deleteRecord = useDeleteRecord();
  const { copyToClipboard, downloadText, handleDocumentDownload, generatingDoc } = useRecordActions(localRecord || record, activeTab);
  const sharedLinksExtended = useSharedLinksExtended();
  
  const dialogs = useRecordDialogs();
  
  const handlers = useRecordHandlers({
    record,
    localRecord,
    setLocalRecord,
    updateProcessedText,
    navigate,
    deleteRecord,
    dialogs,
    setActiveTab,
    refetchRecord,
  });

  useEffect(() => {
    if (record) {
      setLocalRecord(record);
      // Обновляем состояние публикации из данных записи
      setIsPublished(record.isPublished || false);
      setShareUrl(record.shareUrl || null);
    }
  }, [record]);

  const handleReprocess = useCallback(() => dialogs.openDialog('reprocess'), [dialogs]);
  const handleDelete = useCallback(() => dialogs.openDialog('delete'), [dialogs]);
  const handleManageTags = useCallback(() => dialogs.openDialog('tagManagement'), [dialogs]);
  const handleEditTag = useCallback(() => dialogs.openDialog('tagManagement'), [dialogs]);
  
  // Новый обработчик шаринга с интеллектуальной логикой
  const handleShare = useCallback(async () => {
    if (!record?.id) return;
    
    await sharedLinksExtended.handleShareAction({
      recordId: record.id,
      onManageExisting: (existingLink) => {
        setCurrentSharedLink(existingLink);
        setShareManagementDialogOpen(true);
      },
      onCreateNew: () => {
        setShareDialogOpen(true);
      }
    });
  }, [record?.id, sharedLinksExtended]);

  // Обработчики для диалогов управления ссылками
  const handleDeactivateLink = useCallback(async (linkId: string): Promise<boolean> => {
    const success = await sharedLinksExtended.deactivateExistingLink(linkId, () => {
      // После успешной деактивации обновляем локальное состояние
      setIsPublished(false);
      setShareUrl(null);
      setCurrentSharedLink(null);
      // Перезагружаем страницу для получения актуальных данных
      window.location.reload();
    });
    return success;
  }, [sharedLinksExtended]);

  const handleCopyLink = useCallback(async (token: string) => {
    if (!record?.id) return;
    
    const url = await sharedLinksExtended.getSharedLinkUrl(record.id);
    if (url) {
      await navigator.clipboard.writeText(url);
    }
  }, [record?.id, sharedLinksExtended]);

  const handleShareCreated = useCallback(() => {
    // После создания новой ссылки - перезагружаем страницу для получения актуальных данных
    // Альтернативно можно обновить React Query кэш
    if (record?.id) {
      window.location.reload();
    }
  }, [record?.id]);

  // Обработчик изменения состояния редактирования
  const handleEditingStateChange = useCallback((editing: boolean) => {
    setIsEditing(editing);
  }, []);

  // Мемоизируем объекты для пропсов с защитой от null (должно быть до условных возвратов!)
  const tabsRecord = useMemo(() => 
    record ? {
      id: record.id,
      name: record.name,
      transcriptText: record.transcriptText,
      postProcessingText: record.postProcessingText,
    } : null, 
    [record]
  );

  const dialogsRecord = useMemo(() => 
    record ? {
      id: record.id,
      name: record.name,
      templateId: record.templateId,
      tags: record.tags,
    } : null, 
    [record]
  );

  const dialogsLocalRecord = useMemo(() => 
    localRecord ? {
      id: localRecord.id,
      name: localRecord.name,
      templateId: localRecord.templateId,
      tags: localRecord.tags,
    } : null, 
    [localRecord]
  );

  if (loading) {
    return <LoadingState />;
  }

  if (!record) {
    return <RecordNotFound />;
  }

  return (
    <MainLayout disabled={isEditing}>
      <div className="container-page max-w-5xl mx-auto pb-16">
        <RecordHeader
          record={localRecord || record}
          activeTab={activeTab}
          onCopy={copyToClipboard}
          onDownload={downloadText}
          onReprocessSuccess={handlers.handleReprocessSuccess}
          onTagsUpdated={handlers.handleTagsUpdated}
          onReprocess={handleReprocess}
          onDelete={handleDelete}
          onManageTags={handleManageTags}
          onNavigateToRecords={handlers.handleNavigateToRecords}
          onEditTag={handleEditTag}
          onDocumentDownload={handleDocumentDownload}
          onShare={handleShare}
          generatingDoc={generatingDoc}
          isPublished={isPublished}
          disabled={isEditing}
        />

        <RecordTabsSection
          activeTab={activeTab}
          onTabChange={setActiveTab}
          record={tabsRecord!}
          onProcessedTextUpdate={updateProcessedText}
          readOnly={false}
          onEditingStateChange={handleEditingStateChange}
        />
        
        <RecordDialogs
          record={dialogsRecord!}
          localRecord={dialogsLocalRecord}
          dialogs={dialogs}
          handlers={handlers}
          deleteRecord={deleteRecord}
        />
        
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={(open) => {
            setShareDialogOpen(open);
            if (!open) {
              // При закрытии диалога создания проверяем, была ли создана ссылка
              handleShareCreated();
            }
          }}
          recordId={record.id}
          recordName={record.name}
          recordStatus={record.processingStatus || record.status}
        />

        {/* Диалог управления существующей ссылкой */}
        {currentSharedLink && (
          <ShareManagementDialog
            open={shareManagementDialogOpen}
            onOpenChange={setShareManagementDialogOpen}
            recordName={record.name}
            sharedLink={currentSharedLink}
            onDeactivate={handleDeactivateLink}
            onCopyLink={handleCopyLink}
            isLoading={sharedLinksExtended.isLoading}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default RecordDetails;
